import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  useMediaQuery,
  Alert,
  AlertTitle,
  Grid,
  Pagination,
} from "@mui/material";
import { FormEvent, useEffect, useState } from "react";
import { useCharactersContext } from "../hooks/Context";
import LayoutWithIcons from "./LayoutWithIcons";

interface Question {
  updatedBy: string;
  question: string;
  mediaUrl: string;
  difficultyLevel: string;
  answerType: number;
  active: boolean;
  marks: number;
  updatedDate: string;
  subjectId: number;
  questionBankId: number;
  classId: number;
  createdDate: string;
  createdBy: string;
  optionsList: { optionId: number; option: string; correctAnswer: string | boolean }[];
  questionPaperId: number;
  uiType: number;
  remarks: string;
}

interface LayoutProps {
  Questions: Question[];
  sectionName: string;
  currentQuestionIndexProp?: number;
}


const colorThemes = {
  red: {
    base: "#f44336",
    shades: ["#ff8a80", "#ef5350"],
    darkestShade: "#c62828", 
  },
  blue: {
    base: "#2196f3",
    shades: ["#90caf9", "#42a5f5"],
    darkestShade: "#1565c0", 
  },
  green: {
    base: "#4caf50",
    shades: ["#a5d6a7", "#66bb6a"],
    darkestShade: "#2e7d32", 
  },
  purple: {
    base: "#9c27b0",
    shades: ["#ce93d8", "#ab47bc"],
    darkestShade: "#6a1b9a", 
  },
  violet: {
    base: "#673ab7",
    shades: ["#b39ddb", "#7e57c2"], 
    darkestShade: "#4527a0",
  },
};


const getColorTheme = (questionIndex: number) => {
  const themeNames: (keyof typeof colorThemes)[] = ["red", "blue", "purple", "green","violet"];
  const themeIndex = questionIndex % themeNames.length;
  return colorThemes[themeNames[themeIndex]];
};

const Layout19: React.FC<LayoutProps> = ({
  Questions,
  sectionName,
  currentQuestionIndexProp,
}) => {
  const [internalQuestionIndex, setInternalQuestionIndex] = useState(0);
  
  const currentQuestionIndex: number = currentQuestionIndexProp !== undefined 
    ? currentQuestionIndexProp 
    : internalQuestionIndex;
  
  const isGrammarMode = currentQuestionIndexProp !== undefined;
  
  // Navigation handlers for Think & Choose mode
  const handleNavigate = (direction: "previous" | "next") => {
    if (direction === "previous") {
      setInternalQuestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (direction === "next") {
      setInternalQuestionIndex((prevIndex) =>
        Math.min(prevIndex + 1, Questions.length - 1)
      );
    }
  };

  const handlePaginationChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setInternalQuestionIndex(value - 1);
  };
  const {
    questionScores,
    setQuestionScores,
    isViewMode,
    examAnswers,
    setExamAnswers,
  } = useCharactersContext();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width:820px)");
  const [showAlert, setShowAlert] = useState(false);
  const handleAlertClose = () => setShowAlert(false);
  setTimeout(handleAlertClose, 3000);

  const currentQuestion = Questions[currentQuestionIndex];
  const sentences = currentQuestion.question.split("<br>");
  const options = currentQuestion?.optionsList ?? [];
  const correctOptions = options
    .filter((option) => {
      const answer = option.correctAnswer;
      return answer === "true" || answer === true || answer === "True" || String(answer).toLowerCase() === "true";
    })
    .map((option) => option.option);

  const [selectedOptions, setSelectedOptions] = useState<string[]>(() => {
    if (!currentQuestion) return [];
    const storageKey = `selectedOptionsLayout19_${sectionName}_${currentQuestion.questionPaperId}`;
    const savedOptions = localStorage.getItem(storageKey);
    return savedOptions
      ? JSON.parse(savedOptions)
      : Array(Questions.length).fill("");
  });

  // Get color theme for current question based on question index
  const colorTheme = getColorTheme(currentQuestionIndex);
  const shades = colorTheme.shades;
  const darkestShade = colorTheme.darkestShade; // Darkest shade for borders only

  const handleOptionClick = (option: string) => {
    if (isViewMode) return;
    
    setSelectedOptions((prevSelectedOptions) => {
      const updatedOptions = [...prevSelectedOptions];
      updatedOptions[currentQuestionIndex] = option;
      return updatedOptions;
    });
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowAlert(true);

    const selectedOption = selectedOptions[currentQuestionIndex] || "";
    const isCorrect = correctOptions.some(
      (correctOption) => correctOption.toLowerCase().trim() === selectedOption.toLowerCase().trim()
    );

    if (isCorrect) {
      const questionIndex = questionScores.findIndex(
        (item) => item.questionBankId === currentQuestion.questionBankId
      );

      if (questionIndex !== -1) {
        const updatedScores = [...questionScores];
        updatedScores[questionIndex] = {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: currentQuestion.marks,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        };
        setQuestionScores(updatedScores);
      } else {
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: currentQuestion.marks,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      }
    } else {
      setQuestionScores((prevScores) => [
        ...prevScores,
        {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: 0,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        },
      ]);
    }

    // Save exam answers
    const updatedExamAnswer = {
      questionBankId: currentQuestion.questionBankId,
      acutalOptionValue: correctOptions.join(", "),
      enteredOptionValue: selectedOption,
    };

    const existingAnswerIndex = examAnswers.findIndex(
      (answer) => answer.questionBankId === currentQuestion.questionBankId
    );

    if (existingAnswerIndex !== -1) {
      setExamAnswers((prevAnswers) =>
        prevAnswers.map((answer, index) =>
          index === existingAnswerIndex ? { ...updatedExamAnswer } : answer
        )
      );
    } else {
      setExamAnswers((prevAnswers) => [...prevAnswers, updatedExamAnswer]);
    }
  };

  useEffect(() => {
    if (currentQuestion) {
      const storageKey = `selectedOptionsLayout19_${sectionName}_${currentQuestion.questionPaperId}`;
      localStorage.setItem(storageKey, JSON.stringify(selectedOptions));
    }
  }, [selectedOptions, sectionName, currentQuestion?.questionPaperId]);

  useEffect(() => {
    if (isViewMode) {
      const newSelectedOptions = Questions.map((question) => {
        const correct = question.optionsList
          .filter((opt) => {
            const answer = opt.correctAnswer;
            return answer === "true" || answer === true || answer === "True" || String(answer).toLowerCase() === "true";
          })
          .map((opt) => opt.option);
        return correct[0] || "";
      });
      setSelectedOptions(newSelectedOptions);
    }
  }, [currentQuestion, isViewMode, Questions]);

  // Render content based on whether it's in Grammar mode or Think & Choose mode
  const renderContent = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "auto",
        width: "100%",
        boxSizing: "border-box",
        marginTop: isViewMode ? "5%" : isTablet ? "15%" : "auto",
      }}
    >
      {showAlert && (
        <Alert
          severity="success"
          variant="filled"
          sx={{
            position: "fixed",
            top: "30%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "0.5",
          }}
        >
          <AlertTitle sx={{ color: "white" }}>Answer submitted</AlertTitle>
        </Alert>
      )}
      {currentQuestion && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: "12px",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
            width: isMobile ? "92%" : isTablet ? "85%" : "600px",
            maxWidth: "650px",
            margin: "0 auto",
            boxSizing: "border-box",
            pointerEvents: isViewMode ? "none" : "auto",
          }}
        >
          <CardContent sx={{ p: isMobile ? 1.5 : 2.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                paddingBottom: "0px",
              }}
            >
              {/* Question header (line 1) */}
              <Typography
                sx={{
                  fontWeight: "700",
                  fontSize: isMobile ? "0.95rem" : "1.15rem",
                  textAlign: "center",
                  marginBottom: isMobile ? "1vh" : "1.5vh",
                  px: 1,
                }}
              >
                {sentences[0]}
              </Typography>

              {/* Image (if present) */}
              {currentQuestion.mediaUrl ? (
                <Box
                  component="img"
                  src={currentQuestion.mediaUrl}
                  alt="question context"
                  sx={{
                    maxWidth: isMobile ? "60%" : "260px",
                    maxHeight: isMobile ? "120px" : "180px",
                    objectFit: "contain",
                    borderRadius: "8px",
                    mb: 1.5,
                  }}
                />
              ) : null}

              {/* Clickable sentence options */}
              <form onSubmit={handleFormSubmit} style={{ width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: isMobile ? 1.25 : 1.75,
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  {options.map((option, index) => {
                    const isSelected = selectedOptions[currentQuestionIndex] === option.option;
                    const shadeIndex = index % shades.length;
                    const backgroundColor = shades[shadeIndex]; 
                    const textColor = "white"; 
                    const borderColor = isSelected ? darkestShade : backgroundColor; 
                    const borderWidth = isSelected ? "0.25px" : "0px"; 
                    const boxShadow = isSelected ? `0 0 0 2px ${darkestShade}, 0 4px 8px rgba(0,0,0,0.2)` : "none";

                    return (
                      <Button
                        key={option.optionId}
                        onClick={() => handleOptionClick(option.option)}
                        variant="contained"
                        sx={{
                          width: "100%",
                          maxWidth: "550px",
                          minHeight: isMobile ? "44px" : "52px",
                          padding: isMobile ? "8px 12px" : "10px 18px",
                          textTransform: "none",
                          fontSize: isMobile ? "0.85rem" : "0.95rem",
                          lineHeight: 1.35,
                          fontWeight: isSelected ? 700 : 500,
                          backgroundColor: backgroundColor,
                          borderColor: borderColor,
                          borderWidth: borderWidth,
                          borderStyle: "solid",
                          color: textColor,
                          textAlign: "left",
                          justifyContent: "flex-start",
                          boxShadow: boxShadow,
                          borderRadius: "10px",
                          "&:hover": {
                            backgroundColor: backgroundColor,
                            borderColor: isSelected ? darkestShade : borderColor,
                            borderWidth: isSelected ? "0.25px" : borderWidth,
                            opacity: 0.9,
                            transform: isSelected ? "scale(1.02)" : "scale(1.01)",
                            transition: "all 0.2s ease-in-out",
                            boxShadow: isSelected ? `0 0 0 2px ${darkestShade}, 0 6px 12px rgba(0,0,0,0.3)` : boxShadow,
                          },
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          transition: "all 0.2s ease-in-out",
                        }}
                      >
                        {option.option}
                      </Button>
                    );
                  })}
                </Box>

                {!isViewMode && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                    }}
                  >
                    <Button type="submit" variant="contained">
                      Submit
                    </Button>
                  </Box>
                )}
              </form>
            </Box>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // If it's Grammar mode (currentQuestionIndexProp is provided), render without LayoutWithIcons
  if (isGrammarMode) {
    return renderContent();
  }

  // If it's Think & Choose mode, render with LayoutWithIcons and pagination
  return (
    <div>
      <LayoutWithIcons onNavigate={handleNavigate}>
        {renderContent()}
        <Grid container lg={12} md={12} sm={12} xs={12} justifyContent="center">
          <Pagination
            count={Questions.length}
            shape="rounded"
            sx={{
              marginBottom: "1vh",
              paddingTop: "4%",
            }}
            page={currentQuestionIndex + 1}
            onChange={handlePaginationChange}
          />
        </Grid>
      </LayoutWithIcons>
    </div>
  );
};

export default Layout19;

